import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleDot, ClipboardCheck, ChevronRight, UserPlus, Rocket, IdCard, Users, Phone, Mail, MessageSquare, CalendarClock, Settings, Archive, ChevronDown, ChevronUp, PartyPopper } from "lucide-react";
import api, { mediaSrc, avatarFor, ymd, hasShipped } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RecruitInbox } from "@/components/Broadcast";

const PRIORITY_COLOR = { high: "var(--rsp-orange)", medium: "var(--rsp-gold)", low: "var(--rsp-gray)" };
const COMPLETE_FIELDS = ["mos", "rank", "phone", "contact_email", "photo_url", "ship_date", "bct_location"];
const SECTIONS = {
  recruit: [["ship", "Ship Countdown"], ["messages", "Messages"], ["family", "Family Requests"], ["completeness", "Profile Completeness"], ["announcements", "Announcements"], ["media", "Media"]],
  recruiter: [["recruits", "My Recruits"], ["announcements", "Announcements"], ["media", "Media"]],
  admin: [["recruits", "All Recruits"], ["announcements", "Announcements"], ["media", "Media"]],
};

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function shipLabel(iso) {
  const d = daysUntil(iso);
  if (d === null) return null;
  if (d <= 0) return "HAS SHIPPED";
  return `${d} ${d === 1 ? "DAY" : "DAYS"}`;
}

function Stat({ value, label, accent }) {
  return (
    <div className="rsp-card p-3 text-center flex-1">
      <div className="font-mono-rsp text-2xl" style={{ color: accent || "var(--rsp-cream)" }}>{value}</div>
      <div className="eyebrow" style={{ letterSpacing: "0.1em", fontSize: "0.62rem" }}>{label}</div>
    </div>
  );
}

function RecruiterDashboard({ admin = false }) {
  const navigate = useNavigate();
  const [recruits, setRecruits] = useState([]);
  const [q, setQ] = useState("");
  const [recFilter, setRecFilter] = useState("");
  const [showShipped, setShowShipped] = useState(false);

  const load = () => api.get(admin ? "/admin/recruits" : "/me/recruits").then((r) => setRecruits(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [admin]);

  const setCac = async (id, val) => {
    try { await api.put(`/me/recruits/${id}/cac`, { has_cac: val }); load(); toast.success("CAC updated."); }
    catch (e) { toast.error("Could not update CAC."); }
  };

  const recruiterNames = admin
    ? Array.from(new Set(recruits.map((r) => (r.recruiter || "").trim()).filter(Boolean))).sort()
    : [];
  const hasUnassigned = admin && recruits.some((r) => !(r.recruiter || "").trim());

  const scoped = admin && recFilter
    ? recruits.filter((r) => (recFilter === "__unassigned__" ? !(r.recruiter || "").trim() : (r.recruiter || "").trim() === recFilter))
    : recruits;

  const real = scoped.filter((r) => !r.is_demo);
  const shippingSoon = real.filter((r) => { const d = daysUntil(r.ship_date); return d !== null && d > 0 && d <= 30; }).length;
  const cacCount = real.filter((r) => r.has_cac).length;
  const maleCount = real.filter((r) => (r.gender || "").toLowerCase() === "male").length;
  const femaleCount = real.filter((r) => (r.gender || "").toLowerCase() === "female").length;
  const list = scoped
    .filter((r) => (r.name || r.email || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      const da = daysUntil(a.ship_date), dbb = daysUntil(b.ship_date);
      if (da === null && dbb === null) return 0;
      if (da === null) return 1;
      if (dbb === null) return -1;
      return da - dbb;
    });
  const active = list.filter((r) => r.is_demo || !hasShipped(r.ship_date));
  const shipped = list.filter((r) => !r.is_demo && hasShipped(r.ship_date));

  // Mass-contact targets = real recruits (respecting the recruiter filter) who haven't shipped.
  const notShipped = real.filter((r) => !hasShipped(r.ship_date));
  const emails = [...new Set(notShipped.map((r) => (r.contact_email || r.email || "").trim()).filter((e) => e && !e.endsWith("@import.rspriverside.local")))];
  const phones = [...new Set(notShipped.map((r) => (r.phone || "").trim()).filter(Boolean))];
  const emailAll = () => {
    if (!emails.length) return toast.error("No recruit emails available.");
    window.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(","))}`;
  };
  const textAll = () => {
    if (!phones.length) return toast.error("No recruit phone numbers available.");
    window.location.href = `sms:${phones.join(",")}`;
  };

  return (
    <div className="space-y-6" data-testid="recruiter-dashboard">
      <div>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="eyebrow flex items-center gap-2" style={{ letterSpacing: "0.16em" }}><Users size={14} /> {admin ? "All Recruits" : "My Recruits"}</div>
          {admin && (
            <select value={recFilter} onChange={(e) => setRecFilter(e.target.value)} data-testid="dash-recruiter-filter"
              className="rsp-card px-3 py-1.5 text-sm bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]">
              <option value="">All recruiters</option>
              {hasUnassigned && <option value="__unassigned__">Unassigned</option>}
              {recruiterNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
        </div>
        {admin && (
          <div className="flex gap-2 mb-3">
            <button onClick={emailAll} data-testid="dash-email-all-btn" title="Email all recruits who haven't shipped (opens your email app)"
              className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1.5 transition-colors">
              <Mail size={14} /> Email All{emails.length ? ` (${emails.length})` : ""}
            </button>
            <button onClick={textAll} data-testid="dash-text-all-btn" title="Text all recruits who haven't shipped (opens your messaging app)"
              className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1.5 transition-colors">
              <MessageSquare size={14} /> Text All{phones.length ? ` (${phones.length})` : ""}
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <Stat value={real.length} label="Recruits" accent="var(--rsp-gold)" />
          <Stat value={shippingSoon} label="Ship ≤30d" accent="var(--rsp-orange)" />
          <Stat value={`${cacCount}/${real.length || 0}`} label="CAC Issued" accent="#34d399" />
        </div>
        {admin && (
          <div className="flex gap-2 mt-2" data-testid="gender-breakdown">
            <Stat value={real.length} label="Total" accent="var(--rsp-cream)" />
            <Stat value={maleCount} label="Male" accent="#60a5fa" />
            <Stat value={femaleCount} label="Female" accent="#f472b6" />
          </div>
        )}
      </div>

      <div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recruits…"
          className="w-full rsp-card px-4 py-2.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] mb-3" data-testid="dash-recruit-search" />
        <div className="space-y-2">
          {active.map((r) => <DashRecruitCard key={r.user_id} r={r} setCac={setCac} />)}
          {list.length === 0 && (
            <button onClick={() => navigate("/profile")} className="w-full rsp-card p-6 text-center text-[var(--rsp-muted)] hover:border-[var(--rsp-gold)]/60">
              No recruits yet. Manage your recruiter profile in Profile.
            </button>
          )}
          {active.length === 0 && list.length > 0 && (
            <p className="text-[var(--rsp-muted)] text-sm py-2">No active recruits — everyone has shipped. See the archived list below.</p>
          )}
        </div>

        {shipped.length > 0 && (
          <div className="mt-4">
            <button onClick={() => setShowShipped((v) => !v)} data-testid="dash-toggle-shipped"
              className="w-full flex items-center justify-between rsp-card px-4 py-2.5 text-sm text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">
              <span className="flex items-center gap-2 eyebrow" style={{ letterSpacing: "0.14em" }}><Archive size={14} /> Shipped · Archived · {shipped.length}</span>
              {showShipped ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showShipped && (
              <div className="space-y-2 mt-2 opacity-90" data-testid="dash-shipped-list">
                {shipped.map((r) => <DashRecruitCard key={r.user_id} r={r} setCac={setCac} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DashRecruitCard({ r, setCac }) {
  const d = daysUntil(r.ship_date);
  const soon = d !== null && d > 0 && d <= 30;
  return (
    <div className={`rsp-card p-4 ${soon ? "border-l-4" : ""}`} style={soon ? { borderLeftColor: "var(--rsp-orange)" } : {}} data-testid={`dash-recruit-${r.user_id}`}>
      <div className="flex items-center gap-3">
        <img src={r.photo_url ? mediaSrc(r.photo_url) : avatarFor(r)} alt={r.name}
          className="w-11 h-11 rounded-lg object-cover border border-[var(--rsp-border)]" data-testid={`dash-avatar-${r.user_id}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{r.name}{r.is_demo && <span className="ml-2 text-xs orange">SAMPLE</span>}</div>
          <div className="text-[var(--rsp-gray)] text-sm truncate">{[r.rank, r.mos].filter(Boolean).join(" · ") || r.email}</div>
        </div>
        <div className="text-right shrink-0">
          {d !== null ? (
            <div className="flex flex-col items-end">
              <span className={`font-mono-rsp text-lg leading-none ${d <= 0 ? "text-emerald-400" : soon ? "orange" : "gold"}`}>{shipLabel(r.ship_date)}</span>
              {d > 0 && <span className="eyebrow" style={{ letterSpacing: "0.08em", fontSize: "0.58rem" }}>to ship</span>}
            </div>
          ) : <span className="text-[var(--rsp-muted)] text-xs font-head uppercase">No ship date</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className={`inline-flex items-center gap-1 text-xs font-head uppercase px-2 py-1 rounded ${r.has_cac ? "text-emerald-400" : "text-[var(--rsp-muted)]"}`}>
          <IdCard size={14} /> CAC {r.has_cac ? "Issued" : "Pending"}
        </span>
        {r.bct_location && <span className="inline-flex items-center gap-1 text-xs text-[var(--rsp-gray)]"><CalendarClock size={13} /> {r.bct_location}</span>}
        <div className="flex-1" />
        {!r.is_demo && (
          <button onClick={() => setCac(r.user_id, !r.has_cac)} data-testid={`dash-cac-${r.user_id}`}
            className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">
            {r.has_cac ? "Mark CAC pending" : "Mark CAC issued"}
          </button>
        )}
        {r.phone && <a href={`tel:${r.phone}`} className="w-8 h-8 rounded border border-[var(--rsp-border)] flex items-center justify-center text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)]" data-testid={`dash-call-${r.user_id}`}><Phone size={14} /></a>}
        {(r.contact_email || r.email) && <a href={`mailto:${r.contact_email || r.email}`} className="w-8 h-8 rounded border border-[var(--rsp-border)] flex items-center justify-center text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)]" data-testid={`dash-email-${r.user_id}`}><Mail size={14} /></a>}
      </div>
    </div>
  );
}

function ShipCountdown({ user }) {
  const d = daysUntil(user?.ship_date);
  if (d === null) return null;
  const shipped = d <= 0;
  if (shipped) {
    return (
      <div className="rsp-card p-5 fade-up mb-5 relative overflow-hidden" data-testid="ship-celebration"
        style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(52,211,153,0.10))", borderColor: "rgba(212,175,55,0.5)" }}>
        <div className="absolute -right-5 -top-5 opacity-20"><PartyPopper size={110} className="gold" /></div>
        <div className="eyebrow gold mb-2 flex items-center gap-2" style={{ letterSpacing: "0.16em" }}><PartyPopper size={14} /> Mission Underway</div>
        <div className="font-head uppercase gold" style={{ fontSize: "2.2rem", lineHeight: 1.05 }} data-testid="ship-has-shipped">Congrats, you shipped!</div>
        <div className="text-[var(--rsp-cream)] text-sm mt-2 leading-relaxed">
          You've begun your journey — Paratus Preliator. We're proud of you. Stay strong and keep pushing.
        </div>
        <div className="text-[var(--rsp-gray)] text-sm mt-2">
          Shipped {ymd(user.ship_date)}{user.bct_location ? ` · ${user.bct_location}` : ""}
        </div>
      </div>
    );
  }
  return (
    <div className="rsp-card p-5 fade-up mb-5 relative overflow-hidden" data-testid="ship-countdown">
      <div className="absolute -right-6 -top-6 opacity-10"><Rocket size={120} className="gold" /></div>
      <div className="eyebrow gold mb-1" style={{ letterSpacing: "0.16em" }}>Ship Day Countdown</div>
      <div className="flex items-end gap-3">
        <span className="font-mono-rsp gold" style={{ fontSize: "3.4rem", lineHeight: 1 }} data-testid="ship-days">{d}</span>
        <span className="font-head uppercase text-2xl text-[var(--rsp-cream)] mb-1">Days</span>
      </div>
      <div className="text-[var(--rsp-gray)] text-sm mt-1">
        {ymd(user.ship_date)}
        {user.bct_location ? ` · ${user.bct_location}` : ""}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [anns, setAnns] = useState([]);
  const [media, setMedia] = useState([]);
  const [famReqs, setFamReqs] = useState([]);

  const completeness = Math.round(
    (COMPLETE_FIELDS.filter((f) => user?.[f]).length / COMPLETE_FIELDS.length) * 100
  );

  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get("/announcements"), api.get("/media")]);
      setAnns(a.data);
      setMedia(m.data);
    } catch (e) { console.error("Failed to load announcements/media:", e); }
    if (effectiveRole === "recruit") {
      try {
        const fr = await api.get("/guests/pending");
        setFamReqs(fr.data);
      } catch (e) { console.error("Failed to load family requests:", e); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effectiveRole]);

  const act = async (id, action) => {
    try {
      await api.post(`/guests/${id}/${action === "approve" ? "approve" : "revoke"}`);
      setFamReqs((p) => p.filter((f) => f.user_id !== id));
      toast.success(`Family request ${action === "approve" ? "approved" : "denied"}.`);
    } catch (e) { toast.error("Action failed."); }
  };

  const canCustomize = effectiveRole === "recruit" || effectiveRole === "recruiter" || effectiveRole === "admin";
  const sections = SECTIONS[effectiveRole] || [];
  const prefsKey = `rsp_cmd_prefs_${effectiveRole}`;
  const [prefs, setPrefs] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  useEffect(() => { try { setPrefs(JSON.parse(localStorage.getItem(prefsKey) || "{}")); } catch (e) { setPrefs({}); } }, [prefsKey]);
  const vis = (k) => prefs[k] !== false;
  const toggle = (k) => setPrefs((p) => { const n = { ...p, [k]: p[k] === false }; localStorage.setItem(prefsKey, JSON.stringify(n)); return n; });

  return (
    <div className="space-y-6" data-testid="dashboard">
      {canCustomize && (
        <div className="flex items-center justify-between -mb-2">
          <div className="eyebrow" style={{ letterSpacing: "0.16em" }}>Command</div>
          <button data-testid="command-customize" onClick={() => setShowSettings((s) => !s)}
            className="flex items-center gap-1 text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] text-xs font-head uppercase tracking-wide">
            <Settings size={15} /> Customize
          </button>
        </div>
      )}
      {canCustomize && showSettings && (
        <div className="rsp-card p-4" data-testid="command-settings">
          <div className="eyebrow mb-2" style={{ letterSpacing: "0.14em" }}>Show on Command</div>
          <div className="grid grid-cols-2 gap-2">
            {sections.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`cmd-toggle-${k}`}>
                <input type="checkbox" checked={vis(k)} onChange={() => toggle(k)} className="accent-[var(--rsp-gold)]" /> {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {effectiveRole === "recruiter" && vis("recruits") && <RecruiterDashboard />}
      {effectiveRole === "admin" && vis("recruits") && <RecruiterDashboard admin />}

      {vis("ship") && <ShipCountdown user={user} />}

      {effectiveRole === "recruit" && vis("messages") && <RecruitInbox />}

      {effectiveRole === "recruit" && vis("family") && famReqs.length > 0 && (
        <div className="rsp-card p-5 border-l-4" style={{ borderLeftColor: "var(--rsp-orange)" }} data-testid="family-requests">
          <div className="flex items-center gap-2 orange font-head uppercase tracking-wide text-lg mb-3">
            <UserPlus size={18} /> Pending Family Requests · {famReqs.length}
          </div>
          {famReqs.map((f) => (
            <div key={f.user_id} className="flex items-center justify-between gap-3 py-2 border-t border-[var(--rsp-border)] first:border-0">
              <div className="min-w-0">
                <div className="font-semibold truncate">{f.name}</div>
                <div className="text-[var(--rsp-gray)] text-sm truncate">{f.email}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={`family-approve-${f.user_id}`} onClick={() => act(f.user_id, "approve")} className="btn-gold px-4 py-2 text-sm">Approve</button>
                <button data-testid={`family-deny-${f.user_id}`} onClick={() => act(f.user_id, "deny")} className="px-4 py-2 text-sm rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {effectiveRole === "recruit" && vis("completeness") && (
        <button onClick={() => navigate("/profile")} data-testid="completeness-card"
          className="w-full rsp-card p-5 flex items-center gap-4 text-left border-[var(--rsp-gold-dim)]/50 hover:border-[var(--rsp-gold)]">
          <ClipboardCheck className="gold" size={26} />
          <div className="flex-1">
            <div className="font-head uppercase tracking-wide">Complete your profile · {completeness}%</div>
            <div className="mt-2 h-2 rounded-full bg-[var(--rsp-bg-2)] overflow-hidden">
              <div className="h-full bg-[var(--rsp-gold)]" style={{ width: `${completeness}%` }} />
            </div>
          </div>
          <ChevronRight className="text-[var(--rsp-gray)]" />
        </button>
      )}

      {vis("announcements") && (
      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>Important Information</div>
        <div className="space-y-3">
          {anns.map((a) => (
            <div key={a.id} className="rsp-card p-4" data-testid={`announcement-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <CircleDot size={12} style={{ color: PRIORITY_COLOR[a.priority] || "var(--rsp-gold)" }} />
                <h3 className="font-head text-lg uppercase tracking-wide">{a.title}</h3>
              </div>
              <p className="text-[var(--rsp-gray)] leading-relaxed pl-5">{a.body}</p>
            </div>
          ))}
          {anns.length === 0 && <p className="text-[var(--rsp-muted)]">No announcements yet.</p>}
        </div>
      </section>
      )}

      {vis("media") && (
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow" style={{ letterSpacing: "0.16em" }}>Media</div>
          <button onClick={() => navigate("/media")} data-testid="media-see-all" className="font-head uppercase tracking-wide text-sm gold hover:brightness-110">See all</button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {media.slice(0, 6).map((m) => (
            <div key={m.id} className="aspect-square rounded-lg overflow-hidden bg-[var(--rsp-card)] border border-[var(--rsp-border)]" data-testid={`media-${m.id}`}>
              {m.image_url ? (
                <img src={mediaSrc(m.image_url)} alt={m.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--rsp-muted)] text-xs p-2 text-center">{m.title}</div>
              )}
            </div>
          ))}
          {media.length === 0 && <p className="text-[var(--rsp-muted)] col-span-3">No media yet.</p>}
        </div>
      </section>
      )}
    </div>
  );
}
